import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import DataTable from '../../components/admin/DataTable';
import Modal from '../../components/admin/Modal';
import FormField from '../../components/admin/FormField';
import { permissionsService, Permission } from '../../services/admin.service';
import { Save } from 'lucide-react';

const PermissionsPage: React.FC = () => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPermission, setEditingPermission] = useState<Permission | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const [formData, setFormData] = useState({ name: '' });

  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const data = await permissionsService.getAll();
      setPermissions(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingPermission(null);
    setFormData({ name: '' });
    setIsModalOpen(true);
  };

  const handleEdit = (permission: Permission) => {
    setEditingPermission(permission);
    setFormData({ name: permission.name });
    setIsModalOpen(true);
  };

  const handleDelete = async (permission: Permission) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer la permission "${permission.name}"?`)) {
      try {
        await permissionsService.delete(permission.id);
        toast.success('Permission supprimée avec succès');
        loadPermissions();
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingPermission) {
        await permissionsService.update(editingPermission.id, formData);
        toast.success('Permission mise à jour avec succès');
      } else {
        await permissionsService.create(formData);
        toast.success('Permission créée avec succès');
      }
      setIsModalOpen(false);
      loadPermissions();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erreur lors de l\'opération');
    }
  };

  const filteredPermissions = permissions.filter(permission =>
    permission.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  const columns = [
    { key: 'id', header: 'ID' },
    { key: 'name', header: 'Nom' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-text-primary">Gestion des Permissions</h1>
        <p className="text-text-secondary mt-2">Gérer les permissions du système</p>
      </div>

      <DataTable
        data={filteredPermissions}
        columns={columns}
        loading={loading}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingPermission ? 'Modifier la permission' : 'Nouvelle permission'}
        size="md"
      >
        <form onSubmit={handleSubmit}>
          <FormField label="Nom de la permission" required>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
              required
            />
          </FormField>

          <div className="flex items-center justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-text-secondary hover:bg-gray-50 transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors flex items-center space-x-2"
            >
              <Save className="w-4 h-4" />
              <span>{editingPermission ? 'Enregistrer' : 'Créer'}</span>
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default PermissionsPage;

